/**
 * App Configuration
 *
 * Central configuration for the application.
 * This file serves as the source of truth for app identifiers and other
 * environment-specific values.
 */

import { Capacitor } from '@capacitor/core';
import type { PassedInitialConfig, OpenIdConfiguration } from 'angular-auth-oidc-client';
import { LogLevel } from 'angular-auth-oidc-client';

import { APP_CONFIG_REGISTRY } from './app/config/app-config.registry';
import { environment } from './environments/environment';
import type { Environment } from './environments/environment.model';

const BASE_APP_ID = environment.appId;

function getCallbackUrl(): string {
  if (Capacitor.isNativePlatform()) {
    return `${BASE_APP_ID}://callback/`;
  }
  if (environment.environment === 'dev') {
    return `${globalThis.location.origin}/auth-redirect.html`;
  }
  return globalThis.location.origin;
}

function createOidcConfig(configId: string, issuer: string, clientId: string, secureRoutes: Array<string>): OpenIdConfiguration {
  return {
    configId,
    authority: issuer,
    redirectUrl: getCallbackUrl(),
    postLogoutRedirectUri: getCallbackUrl(),
    clientId,
    scope: `${clientId} openid offline_access`,
    responseType: 'code',
    silentRenew: true,
    useRefreshToken: true,
    renewTimeBeforeTokenExpiresInSeconds: 60,
    allowUnsafeReuseRefreshToken: true,
    customParamsRefreshTokenRequest: {
      redirect_uri: getCallbackUrl(),
    },
    issValidationOff: true,
    strictIssuerValidationOnWellKnownRetrievalOff: true,
    disableIdTokenValidation: false,
    disableIatOffsetValidation: true,
    disableRefreshIdTokenAuthTimeValidation: true,
    ignoreNonceAfterRefresh: true,
    unauthorizedRoute: '/login',
    postLoginRoute: '',
    //    logLevel: LogLevel.Debug,
    logLevel: LogLevel.None,
    customParamsAuthRequest: {
      prompt: 'login',
      response_mode: 'query',
    },
    autoUserInfo: false,
    secureRoutes,
  };
}

function buildOidcConfigs(): Array<OpenIdConfiguration> {
  return APP_CONFIG_REGISTRY.filter(entry => entry.availableIn.includes(environment.environment)).map(entry =>
    createOidcConfig(entry.key, entry.config.auth.issuer, entry.config.auth.clientId, Object.values(entry.config.backend))
  );
}

export const APP_CONFIG = {
  baseAppId: BASE_APP_ID,

  authorization: {
    config: buildOidcConfigs(),
  } as PassedInitialConfig,
};
